// Muscle-factor overrides: how the volume calculator is told what an exercise trains
// when the sub-slot it sits in cannot answer that question by itself.
//
// Weekly volume per muscle is worked out per SLOT, not per exercise: `getMuscleVolumeForPlan`
// looks the sub-slot's slot up in `getExerciseConstantsTable()` and multiplies that slot's
// muscle weights by the number of sets. A sub-slot whose slot has no entry in that table
// contributes nothing at all — that is the `slot/$custom` stand-in, used for an exercise the
// app could not place in a real slot. An override supplies the missing muscle weights for one
// sub-slot, and is honoured both by the volume display and by the volume re-optimisation,
// which passes the same map to the solver.
//
// This module holds the rules and nothing else. Its callers hand it the two lookups it needs,
// so it imports no Angular service and can be unit-tested directly — the same split, and for
// the same reason, as `muscle-volume-rows.ts`. The thin adapter that wires it to the real
// services lives in `muscle-volume-helpers.ts`.

import { FitnessPlan, parseSubslotIdAndOffset } from '@cf-shared/data-model/fitness-plan';
import { isCustomExerciseById } from '@cf-shared/data-model/exercise';
import { ExerciseConstantsTable, MuscleFactors } from '@cf-shared/workout-solver-interface';

/** The part of a custom-exercise document these rules read. */
export interface CustomExerciseAttribution {
  _id: string;
  /** the exercise the user picked as "Most Similar Exercise", if any. Usually a catalog
   *  exercise, but it can be another of the user's own exercises (see `resolveSimilarExercise`) */
  similarExerciseId?: string;
}

/** Where following an own exercise's "Most Similar Exercise" link ended up. */
export interface SimilarExerciseResolution {
  /** the catalog exercise the link leads to, or `undefined` when it never reaches one */
  catalogExerciseId?: string;
  /** every own exercise id walked, starting with the one asked about. When the link never
   *  reaches a catalog exercise, the last id is where it stopped: a repeated id for a loop, an id
   *  with no document, or an own exercise with no similar exercise of its own */
  chain: string[];
}

/**
 * Follow an own exercise's "Most Similar Exercise" link until it reaches a catalog exercise.
 *
 * The link can name another of the user's own exercises. The workout-plan scanner does this when
 * its AI suggests one of them as a substitute, and coach forks and the id rename keep such links
 * (CYBERNETIC-FITNESS-APP-463). That exercise borrows its muscles the same way, so the link is
 * followed on until a catalog exercise answers. Returns `undefined` when the exercise has no
 * similar exercise set at all.
 */
export function resolveSimilarExercise(
  customExerciseId: string,
  customExercisesById: Map<string, CustomExerciseAttribution>,
): SimilarExerciseResolution | undefined {
  const start = customExercisesById.get(customExerciseId);
  if (!start?.similarExerciseId) return undefined;
  const chain = [customExerciseId];
  const visited = new Set(chain);
  let exerciseId = start.similarExerciseId;
  // while the link still names an own exercise, step through it
  while (isCustomExerciseById(exerciseId)) {
    chain.push(exerciseId);
    // if we have been here before, the links form a loop and no catalog exercise is reachable
    if (visited.has(exerciseId)) return { chain };
    visited.add(exerciseId);
    const next = customExercisesById.get(exerciseId)?.similarExerciseId;
    // if the document is gone or names no similar exercise, the chain stops here
    if (!next) return { chain };
    exerciseId = next;
  }
  return { catalogExerciseId: exerciseId, chain };
}

/** The two questions these rules have to ask the databases. */
export interface MuscleAttributionLookups {
  /** every custom exercise this user has */
  getCustomExercisesAsync(): Promise<CustomExerciseAttribution[]>;
  /** the short slot id (`$chin_up`) the exercise belongs to, or `undefined` if no slot lists it */
  slotShortIdForExerciseAsync(exerciseId: string): Promise<string | undefined>;
}

/**
 * Build the per-sub-slot muscle-factor overrides for a plan, or `undefined` when none are
 * needed.
 *
 * Two rules, one per kind of exercise:
 *
 * - **A user's own exercise** that has a "Most Similar Exercise" set borrows that exercise's
 *   slot's muscle weights (#3790). When the similar exercise is another of the user's own
 *   exercises, its link is followed on to a catalog exercise (`resolveSimilarExercise`).
 * - **A curated exercise sitting in a slot that carries no muscle weights** borrows the
 *   weights of the slot the catalog says it belongs to (#4978). This is for the exercise the
 *   app could not place when it was added — it went under the `slot/$custom` stand-in and has
 *   counted zero ever since. Once the catalog gives it a slot, this rule makes its sets count
 *   for everybody already holding it, with nothing written to their saved program.
 *
 * The two rules differ on one point. The curated rule never touches a slot that already
 * carries its own muscle weights, because that would silently change what an ordinary
 * exercise is counted as. The custom rule has no such limit and never has: a user's own
 * exercise overrides whatever slot it sits in, including a real one, because the exercise
 * they named as most similar is a better answer than the slot they happened to swap it into.
 *
 * When `sourceSlots` is passed, it is filled with the slot each override was copied from. The
 * volume re-optimisation hands it to the solver, which reads that slot's prime movers to decide
 * whether a Maintain or Shrink muscle makes the exercise cost more (#5398).
 *
 * When `similarSlots` is passed, it is filled, per user's own exercise id, with the slot of the
 * catalog exercise its "Most Similar Exercise" link leads to, whether or not that slot carries muscle weights. The
 * uncounted-exercise report reads it to tell a similar exercise in a slot the program does not
 * measure (expected) from one that should have given weights and did not (#6060).
 */
export async function buildMuscleFactorOverridesForPlanAsync(
  fitnessPlan: FitnessPlan,
  constantsTable: ExerciseConstantsTable,
  lookups: MuscleAttributionLookups,
  sourceSlots?: { [subSlotId: string]: string },
  similarSlots?: { [customExerciseId: string]: string },
): Promise<{ [subSlotId: string]: MuscleFactors } | undefined> {
  let overrides: { [subSlotId: string]: MuscleFactors } | undefined;
  const customExercises = await lookups.getCustomExercisesAsync();
  const customExercisesById = new Map(customExercises.map(e => [e._id, e]));
  for (const workout of fitnessPlan.workouts) {
    for (const subSlotId of workout.subSlotIds) {
      const subSlot = fitnessPlan.subSlots[subSlotId];
      if (!subSlot) continue;

      // which exercise do we ask about? a user's own exercise answers through the catalog
      // exercise its "Most Similar Exercise" link leads to; a curated one answers for itself
      let exerciseIdToPlace: string;
      if (isCustomExerciseById(subSlot.exercise_id)) {
        const catalogExerciseId = resolveSimilarExercise(subSlot.exercise_id, customExercisesById)?.catalogExerciseId;
        if (!catalogExerciseId) continue;
        exerciseIdToPlace = catalogExerciseId;
      } else {
        // read the slot the same way the volume calculator does — off the sub-slot id, not off
        // `subSlot.slotId` — so the two can never disagree about which slot was consulted
        const { parsedSlotId } = parseSubslotIdAndOffset(subSlotId);
        if (constantsTable[parsedSlotId]) continue; // this slot already says what it trains
        exerciseIdToPlace = subSlot.exercise_id;
      }

      const refSlotShortId = await lookups.slotShortIdForExerciseAsync(exerciseIdToPlace);
      if (!refSlotShortId) continue;
      // if this is the user's own exercise, remember which slot its similar exercise is in
      if (similarSlots && isCustomExerciseById(subSlot.exercise_id)) similarSlots[subSlot.exercise_id] = refSlotShortId;
      const refConstants = constantsTable[refSlotShortId];
      if (!refConstants) continue;
      if (!overrides) overrides = {};
      overrides[subSlotId] = refConstants.muscleFactors;
      if (sourceSlots) sourceSlots[subSlotId] = refSlotShortId;
    }
  }
  return overrides;
}
